import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    Archive,
    Copy,
    Edit,
    Facebook,
    Globe,
    Loader2,
    Megaphone,
    Pause,
    Play,
    RefreshCw,
    Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { useConfirm } from '@/hooks/useConfirm';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import {
    useCampaign,
    useRotateCampaignToken,
    useUpdateCampaignStatus,
} from '@/api/campaigns/use-campaigns';
import {
    useCampaignSubmissions,
    useReprocessFailedCampaignSubmissions,
} from '@/api/campaigns/use-campaign-submissions';
import {
    CampaignStatus,
    CampaignSubmission,
    CampaignSubmissionStatus,
} from '@/api/campaigns/types';
import { getCampaignStatusActions } from '@/lib/campaignStatus';
import { getMetaAdValue, getSubmissionIssueCount } from '@/lib/campaignSubmission';
import SubmissionLeadLink from '@/components/campaigns/submissions/SubmissionLeadLink';
import { extractErrorMessages } from '@/components/campaigns/extractErrorMessages';
import SubmissionDetailsDialog from '@/components/campaigns/submissions/SubmissionDetailsDialog';

const SUBMISSIONS_PAGE_SIZE = 25;

/**
 * Campaign detail page (quantix-api/docs/campaigns/TASKS.md, task W4): header with
 * status actions, submission stats, campaign info (endpoint URL / Facebook form),
 * and a paginated, filterable submissions table with a detail drawer and reprocess.
 */
const CampaignDetailsPage: React.FC = () => {
    const { t } = useTranslation('crm');
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const { campaignId } = useParams<{ campaignId: string }>();

    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';

    const canEdit = hasPermission(ModulePermission.CAMPAIGNS, PermissionAction.EDIT);

    const { data, isLoading } = useCampaign(campaignId ?? '');
    const campaign = data?.data;

    const [activationErrors, setActivationErrors] = useState<string[]>([]);
    const [submissionStatus, setSubmissionStatus] = useState<CampaignSubmissionStatus | ''>('');
    const [submissionPage, setSubmissionPage] = useState(1);
    const [selectedSubmission, setSelectedSubmission] = useState<CampaignSubmission | null>(null);

    const { data: submissionsData, isLoading: isLoadingSubmissions } = useCampaignSubmissions(
        campaignId ?? '',
        {
            page: submissionPage,
            per_page: SUBMISSIONS_PAGE_SIZE,
            status: submissionStatus || undefined,
        }
    );
    const submissions = submissionsData?.data ?? [];
    const totalSubmissions = submissionsData?.meta?.total ?? 0;
    const totalSubmissionPages = submissionsData?.meta?.last_page ?? 1;

    const updateStatusMutation = useUpdateCampaignStatus();
    const rotateTokenMutation = useRotateCampaignToken();
    const reprocessFailedMutation = useReprocessFailedCampaignSubmissions();

    const handleChangeStatus = async (status: CampaignStatus) => {
        if (!campaign) return;
        setActivationErrors([]);
        try {
            await updateStatusMutation.mutateAsync({ id: campaign.id, status });
            enqueueSnackbar(t('campaigns.toasts.statusUpdated'), { variant: 'success' });
        } catch (error) {
            if (status === 'active') {
                const body = (error as { response?: { data?: unknown } })?.response?.data;
                const messages = extractErrorMessages(body);
                setActivationErrors(
                    messages.length > 0 ? messages : [t('campaigns.detail.activateFailed')]
                );
            }
            enqueueSnackbar(t('campaigns.toasts.statusUpdateFailed'), { variant: 'error' });
        }
    };

    const handleArchive = async () => {
        if (!campaign) return;
        const ok = await confirm({
            title: t('campaigns.confirm.archiveTitle'),
            description: t('campaigns.confirm.archiveDescription', { name: campaign.name }),
            confirmLabel: t('campaigns.actions.archive'),
        });
        if (!ok) return;
        handleChangeStatus('archived');
    };

    const handleCopyEndpoint = async () => {
        if (!campaign?.endpoint_url) return;
        try {
            await navigator.clipboard.writeText(campaign.endpoint_url);
            enqueueSnackbar(t('campaigns.detail.info.urlCopied'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.detail.info.copyFailed'), { variant: 'error' });
        }
    };

    const handleRotateToken = async () => {
        if (!campaign) return;
        const ok = await confirm({
            title: t('campaigns.detail.info.rotateConfirmTitle'),
            description: t('campaigns.detail.info.rotateConfirmDescription'),
            confirmLabel: t('campaigns.detail.info.rotateToken'),
        });
        if (!ok) return;
        try {
            await rotateTokenMutation.mutateAsync(campaign.id);
            enqueueSnackbar(t('campaigns.detail.info.rotated'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.detail.info.rotateFailed'), { variant: 'error' });
        }
    };

    const handleReprocessFailed = async () => {
        if (!campaign) return;
        const ok = await confirm({
            title: t('campaigns.detail.submissions.reprocessFailedConfirmTitle'),
            description: t('campaigns.detail.submissions.reprocessFailedConfirmDescription'),
            confirmLabel: t('campaigns.detail.submissions.reprocessFailed'),
        });
        if (!ok) return;
        try {
            const result = await reprocessFailedMutation.mutateAsync(campaign.id);
            enqueueSnackbar(
                t('campaigns.detail.submissions.reprocessedToast', { count: result.reprocessed }),
                { variant: 'success' }
            );
        } catch {
            enqueueSnackbar(t('campaigns.detail.submissions.reprocessFailedActionFailed'), {
                variant: 'error',
            });
        }
    };

    const submissionFilterFields = useMemo(
        () => [
            {
                key: 'status',
                label: t('campaigns.detail.submissions.filters.status'),
                type: 'select' as const,
                options: (
                    ['pending', 'lead_created', 'linked_existing', 'failed', 'ignored'] as const
                ).map(status => ({
                    value: status,
                    label: t(`campaigns.detail.submissions.statusOptions.${status}`),
                })),
            },
        ],
        [t]
    );

    const submissionColumns: Column<CampaignSubmission>[] = useMemo(
        () => [
            {
                id: 'received_at',
                label: t('campaigns.detail.submissions.columns.receivedAt'),
                key: 'received_at',
                width: '180px',
                render: value => (
                    <DateTimeFormatter date={value} timeZone={crmTimezone} className="text-sm" />
                ),
            },
            {
                id: 'status',
                label: t('campaigns.detail.submissions.columns.status'),
                key: 'status',
                width: '140px',
                render: value => <StatusBadge status={value} kind="submission" />,
            },
            {
                id: 'lead',
                label: t('campaigns.detail.submissions.columns.lead'),
                key: 'lead_id',
                render: (_, submission) => (
                    <SubmissionLeadLink submission={submission} stopPropagation />
                ),
            },
            ...(campaign?.source === 'facebook'
                ? (
                      [
                          ['ad', 'ad_name'],
                          ['adSet', 'adset_name'],
                          ['fbCampaign', 'campaign_name'],
                      ] as const
                  ).map(([column, metaKey]) => ({
                      id: `meta_${metaKey}`,
                      label: t(`campaigns.detail.submissions.columns.${column}`),
                      key: 'id' as const,
                      render: (_: unknown, submission: CampaignSubmission) =>
                          getMetaAdValue(submission, metaKey) ?? (
                              <span className="text-muted-foreground">—</span>
                          ),
                  }))
                : []),
            {
                id: 'is_test',
                label: t('campaigns.detail.submissions.columns.test'),
                key: 'is_test',
                width: '90px',
                render: value =>
                    value ? (
                        <Badge variant="outline">
                            {t('campaigns.detail.submissions.testBadge')}
                        </Badge>
                    ) : null,
            },
            {
                id: 'issues',
                label: t('campaigns.detail.submissions.columns.issues'),
                key: 'id',
                width: '90px',
                render: (_, submission) => {
                    const count = getSubmissionIssueCount(submission);
                    return count > 0 ? (
                        <Badge variant="secondary">{count}</Badge>
                    ) : (
                        <span className="text-muted-foreground">—</span>
                    );
                },
            },
        ],
        [t, crmTimezone, campaign?.source]
    );

    if (isLoading) {
        return (
            <PageContainer>
                <div className="flex items-center gap-4">
                    <Skeleton className="h-9 w-32 rounded-md" />
                    <Skeleton className="h-8 w-48" />
                </div>
                <StatTileGrid>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 rounded-xl" />
                    ))}
                </StatTileGrid>
            </PageContainer>
        );
    }

    if (!campaign) {
        return (
            <PageContainer>
                <PageHeader
                    title={t('campaigns.detail.notFound')}
                    backTo="/campaigns"
                    backLabel={t('campaigns.detail.backToCampaigns')}
                />
            </PageContainer>
        );
    }

    const { canActivate, canPause, canArchive } = getCampaignStatusActions(campaign.status);
    const counts = campaign.submissions_count;

    return (
        <PageContainer>
            <PageHeader
                title={campaign.name}
                backTo="/campaigns"
                backLabel={t('campaigns.detail.backToCampaigns')}
                meta={
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="gap-1 font-normal">
                            {campaign.source === 'facebook' ? (
                                <Facebook className="h-3 w-3" />
                            ) : (
                                <Globe className="h-3 w-3" />
                            )}
                            {t(`campaigns.source.${campaign.source}`)}
                        </Badge>
                        <StatusBadge status={campaign.status} kind="campaign" />
                    </div>
                }
                actions={
                    <>
                        {canEdit && (
                            <Button
                                variant="outline"
                                onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                            >
                                <Edit className="h-4 w-4" />
                                {t('campaigns.detail.actions.edit')}
                            </Button>
                        )}
                        {canEdit && canActivate && (
                            <Button
                                onClick={() => handleChangeStatus('active')}
                                disabled={updateStatusMutation.isPending}
                            >
                                <Play className="h-4 w-4" />
                                {t('campaigns.detail.actions.activate')}
                            </Button>
                        )}
                        {canEdit && canPause && (
                            <Button
                                variant="outline"
                                onClick={() => handleChangeStatus('paused')}
                                disabled={updateStatusMutation.isPending}
                            >
                                <Pause className="h-4 w-4" />
                                {t('campaigns.detail.actions.pause')}
                            </Button>
                        )}
                        {canEdit && canArchive && (
                            <Button
                                variant="outline"
                                onClick={handleArchive}
                                disabled={updateStatusMutation.isPending}
                            >
                                <Archive className="h-4 w-4" />
                                {t('campaigns.detail.actions.archive')}
                            </Button>
                        )}
                    </>
                }
            />

            {activationErrors.length > 0 && (
                <Alert variant="destructive">
                    <AlertDescription>
                        <ul className="list-disc space-y-0.5 pl-4">
                            {activationErrors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            <StatTileGrid>
                <StatTile
                    label={t('campaigns.detail.stats.total')}
                    value={counts?.total ?? 0}
                    icon={Target}
                    tone="brand"
                />
                <StatTile
                    label={t('campaigns.detail.stats.leadCreated')}
                    value={counts?.lead_created ?? 0}
                    icon={Target}
                    tone="success"
                />
                <StatTile
                    label={t('campaigns.detail.stats.linkedExisting')}
                    value={counts?.linked_existing ?? 0}
                    icon={Target}
                    tone="info"
                />
                <StatTile
                    label={t('campaigns.detail.stats.failed')}
                    value={counts?.failed ?? 0}
                    icon={Target}
                    tone="destructive"
                />
                <StatTile
                    label={t('campaigns.detail.stats.pending')}
                    value={counts?.pending ?? 0}
                    icon={Target}
                    tone="warning"
                />
            </StatTileGrid>

            <Card>
                <CardHeader>
                    <CardTitle>{t('campaigns.detail.info.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <p className="text-sm text-muted-foreground">
                            {t('campaigns.detail.info.description')}
                        </p>
                        <p className="text-sm">
                            {campaign.description || t('campaigns.detail.info.noDescription')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <p className="text-sm text-muted-foreground">
                                {t('campaigns.detail.info.defaultStatus')}
                            </p>
                            <p className="text-sm font-medium">
                                {campaign.default_lead_status?.label ??
                                    t('campaigns.detail.info.systemDefault')}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">
                                {t('campaigns.detail.info.defaultAssignee')}
                            </p>
                            <p className="text-sm font-medium">
                                {campaign.default_assignee?.name ?? t('campaigns.detail.info.none')}
                            </p>
                        </div>
                    </div>

                    {campaign.source === 'landing_page' && campaign.endpoint_url && (
                        <div className="space-y-2 border-t pt-4">
                            <p className="text-sm text-muted-foreground">
                                {t('campaigns.detail.info.endpointTitle')}
                            </p>
                            <div className="flex items-center gap-2">
                                <Input
                                    readOnly
                                    value={campaign.endpoint_url}
                                    className="font-mono text-xs"
                                />
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={handleCopyEndpoint}
                                    aria-label={t('campaigns.detail.info.copyUrl')}
                                    title={t('campaigns.detail.info.copyUrl')}
                                >
                                    <Copy className="h-4 w-4" />
                                </Button>
                                {canEdit && (
                                    <Button
                                        variant="outline"
                                        onClick={handleRotateToken}
                                        disabled={rotateTokenMutation.isPending}
                                    >
                                        {rotateTokenMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-4 w-4" />
                                        )}
                                        {t('campaigns.detail.info.rotateToken')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {campaign.source === 'facebook' && (
                        <div className="grid grid-cols-1 gap-4 border-t pt-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm text-muted-foreground">
                                    {t('campaigns.detail.info.facebookPage')}
                                </p>
                                <p className="text-sm font-medium">
                                    {campaign.facebook_page?.name ??
                                        t('campaigns.detail.info.none')}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">
                                    {t('campaigns.detail.info.facebookForm')}
                                </p>
                                <p className="text-sm font-medium">
                                    {campaign.facebook_form_name ?? t('campaigns.detail.info.none')}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">
                                    {t('campaigns.detail.info.formStatus')}
                                </p>
                                <p className="text-sm font-medium">
                                    {campaign.facebook_form_status ??
                                        t('campaigns.detail.info.none')}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">
                                    {t('campaigns.detail.info.lastPolled')}
                                </p>
                                {campaign.facebook_last_polled_at ? (
                                    <DateTimeFormatter
                                        date={campaign.facebook_last_polled_at}
                                        timeZone={crmTimezone}
                                        className="text-sm font-medium"
                                    />
                                ) : (
                                    <p className="text-sm font-medium">
                                        {t('campaigns.detail.info.neverPolled')}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{t('campaigns.detail.submissions.title')}</h2>
                {canEdit && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReprocessFailed}
                        disabled={reprocessFailedMutation.isPending || (counts?.failed ?? 0) === 0}
                    >
                        {reprocessFailedMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        {t('campaigns.detail.submissions.reprocessFailed')}
                    </Button>
                )}
            </div>

            <GenericDataTable<CampaignSubmission>
                data={submissions}
                columns={submissionColumns}
                visibleColumns={submissionColumns.map(col => col.id)}
                filters={{ status: submissionStatus }}
                filterFields={submissionFilterFields}
                presets={[]}
                isLoading={isLoadingSubmissions}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={submission => submission.id}
                sortConfig={{ key: 'received_at', direction: 'desc' }}
                onSort={() => {}}
                totalItems={totalSubmissions}
                totalPages={totalSubmissionPages}
                currentPage={submissionPage}
                pageSize={SUBMISSIONS_PAGE_SIZE}
                onPageChange={setSubmissionPage}
                showExportButton={false}
                onRowClick={submission => setSelectedSubmission(submission)}
                onFilterChange={filters => {
                    setSubmissionStatus((filters.status as CampaignSubmissionStatus) || '');
                    setSubmissionPage(1);
                }}
                onClearFilters={() => {
                    setSubmissionStatus('');
                    setSubmissionPage(1);
                }}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage={t('campaigns.detail.submissions.empty.title')}
                loadingMessage={t('table.loading')}
                emptyState={
                    <EmptyState
                        icon={Megaphone}
                        title={t('campaigns.detail.submissions.empty.title')}
                        description={
                            submissionStatus
                                ? t('campaigns.detail.submissions.empty.noMatches')
                                : undefined
                        }
                    />
                }
            />

            <SubmissionDetailsDialog
                campaignId={campaign.id}
                isFacebook={campaign.source === 'facebook'}
                submission={selectedSubmission}
                crmTimezone={crmTimezone}
                canEdit={canEdit}
                onOpenChange={open => {
                    if (!open) setSelectedSubmission(null);
                }}
            />
        </PageContainer>
    );
};

export default CampaignDetailsPage;
